import Box from '@mui/joy/Box'
import CssBaseline from '@mui/joy/CssBaseline'
import GlobalStyles from '@mui/joy/GlobalStyles'
import Typography from '@mui/joy/Typography'
import { CssVarsProvider, useColorScheme } from '@mui/joy/styles'
import { Children } from 'types'
import ColorSchemeToggle from '../components/ColorSchemeToggle'

// This component will be used inside the CssVarsProvider
function AuthContent({ children }: Children) {
    const { mode } = useColorScheme()
    const logo = mode === 'dark' ? '/numeo-logo-dark.png' : '/numeo-logo.png'
    const siteName = 'Numeo Spot'

    return (
        <>
            <CssBaseline />
            <GlobalStyles
                styles={{
                    ':root': {
                        '--Collapsed-breakpoint': '769px', // form will stretch when viewport is below `769px`
                        '--Cover-width': '50vw', // must be `vw` only
                        '--Form-maxWidth': '400px',
                        '--Transition-duration': '0.4s', // set to `none` to disable transition
                    },
                    'html, body': {
                        height: '100%',
                    },
                }}
            />
            <Box
                sx={(theme) => ({
                    width: 'clamp(100vw - var(--Cover-width), (var(--Collapsed-breakpoint) - 100vw) * 999, 100vw)',
                    transition: 'width var(--Transition-duration)',
                    transitionDelay: 'calc(var(--Transition-duration) + 0.1s)',
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'rgba(255 255 255 / 0.8)',
                    [theme.getColorSchemeSelector('dark')]: {
                        backgroundColor: 'rgba(19 19 24 / 0.3)',
                    },
                })}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minHeight: '100dvh',
                        width: '100%',
                        maxWidth: '100%',
                        px: { xs: 2, sm: 4 },
                    }}
                >
                    <Box
                        component="header"
                        sx={{
                            py: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            maxWidth: '550px',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img alt={`${siteName} Logo`} src={logo} width="160" height="auto" />
                        </Box>

                        <ColorSchemeToggle />
                    </Box>
                    <Box
                        sx={{
                            width: '100%',
                            height: 'calc(100vh - 250px)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            maxWidth: '550px',
                            '& .MuiFormLabel-root': {
                                fontSize: '1rem',
                            },
                            '& .MuiInput-root': {
                                fontSize: '1.1rem',
                            },
                            '& .MuiButton-root': {
                                fontSize: '1rem',
                                padding: '0.75rem',
                            },
                        }}
                    >
                        {children}
                    </Box>
                    <Box component="footer" sx={{ py: 4, mt: 'auto', width: '100%', maxWidth: '550px' }}>
                        <Typography level="body-xs" textAlign="center">
                            © {siteName.replace(' AI Agents', '')} {new Date().getFullYear()}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={(theme) => ({
                    height: '100%',
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    left: 'clamp(0px, (100vw - var(--Collapsed-breakpoint)) * 999, 100vw - var(--Cover-width))',
                    transition: 'background-image var(--Transition-duration), left var(--Transition-duration) !important',
                    transitionDelay: 'calc(var(--Transition-duration) + 0.1s)',
                    backgroundColor: 'background.level1',
                    backgroundPosition: 'right center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundImage: 'url(/main-background.png)',
                    [theme.getColorSchemeSelector('dark')]: {
                        backgroundImage: 'url(/main-background.png)',
                    },
                })}
            />
        </>
    )
}

export default function AuthLayout({ children }: Children) {
    return (
        <CssVarsProvider disableTransitionOnChange>
            <AuthContent children={children} />
        </CssVarsProvider>
    )
}
